import { AppDispatch } from 'src/store/store';
import { setGlobalInfo } from 'src/store/features/global';

export class GlobalUtils {
    // Decode JWT payload safely
    public static decodeJwt(token: string): any | null {
        try {
            if (!token) return null;
            const parts = token.split('.');
            if (parts.length !== 3) return null;
            const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const decoded = decodeURIComponent(
              atob(payload)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            );
            return JSON.parse(decoded);
        } catch {
            return null;
        }
    }

    // Infer app role string from JWT roles array
    // Backend sets: claim("roles", listOfStringRoleNames)
    public static getAppRoleFromToken(token: string | null | undefined): string {
        try {
            if (!token) return '';
            const payload = GlobalUtils.decodeJwt(token);
            const roles: any[] = payload?.roles || [];
            if (!Array.isArray(roles) || roles.length === 0) return '';

            const roleNames = roles
              .map((r: any) => (typeof r === 'string' ? r : String(r || '')))
              .filter(Boolean);

            if (roleNames.includes('ROLE_SUPPER_ADMIN')) return 'SUPER_ADMIN';
            if (roleNames.includes('ROLE_ADMIN')) return 'ADMIN';
            if (roleNames.includes('ROLE_CONSULTANT')) return 'CONSULTANT';
            if (roleNames.includes('ROLE_USER')) return 'USER';

            return '';
        } catch {
            return '';
        }
    }

    // Extract approved flag and application status from JWT (if present)
    public static getAuthStateFromToken(token: string | null | undefined): {
      role: string;
      approved: boolean | null;
      applicationStatus: string;
    } {
      const payload = token ? GlobalUtils.decodeJwt(token) : null;

      const role = GlobalUtils.getAppRoleFromToken(token);

      const approvedRaw = payload?.isApproved as boolean | string | undefined;
      let approved: boolean | null = null;
      if (typeof approvedRaw === 'boolean') {
        approved = approvedRaw;
      } else if (typeof approvedRaw === 'string') {
        const lower = approvedRaw.toLowerCase();
        if (lower === 'true' || lower === 'approved') approved = true;
        else if (lower === 'false' || lower === 'pending' || lower === 'rejected') approved = false;
      }

      const applicationStatus = (payload?.applicationStatus || '') as string;

      return {
        role,
        approved,
        applicationStatus: String(applicationStatus || ''),
      };
    }

    //Token handling
    public static setToken(token: string) {
        if (token) {
            window.localStorage.setItem("authToken", token);
        }
    }

    public static getToken(): string {
        return window.localStorage.getItem("authToken") || "";
    }

    public static clearToken(): void {
        window.localStorage.removeItem("authToken");
    }

    //User Role
    public static setUserRole(role: string): void {
        if (role) {
            window.localStorage.setItem("userRole", role);
        }
    }

    public static getUserRole(): string {
        return window.localStorage.getItem("userRole") || "";
    }

    public static clearUserRole(): void {
        window.localStorage.removeItem("userRole");
    }

    // User info
    public static setUserLoginInfo(emailId: string, mobileNo: string): void {
        if (emailId && mobileNo) {
            window.localStorage.setItem("accountInfo", JSON.stringify({ emailId, mobileNo }));
        }
    }

    public static getUserLoginInfo(): { emailId: string, mobileNo: string } | null {
        const accountInfo = window.localStorage.getItem("accountInfo");
        return accountInfo ? JSON.parse(accountInfo) : null;
    }

    //Global language (can stay in session if you prefer)
    public static setGlobalLanguage(dispatch: AppDispatch): void {
        try {
            const lang = (typeof window !== "undefined" && window.localStorage)
                ? window.localStorage.getItem("lang") || "EN"
                : "EN";
            dispatch(setGlobalInfo({ globalLanguage: lang }));
        } catch {
            dispatch(setGlobalInfo({ globalLanguage: "EN" }));
        }
    }

    // Helper: check if login response indicates rejected status
    public static isRejectedLogin(resp: any): boolean {
      try {
        const approved = String((resp || {}).approved || "").toLowerCase();
        const meta = (resp || {}).metaData || {};
        const rawStatus = String(
          meta?.ApplicationStatus || (resp || {}).applicationStatus || (resp || {}).status || ""
        ).toLowerCase();
        const flag = Boolean((resp || {}).rejected === true);
        return flag || approved === "rejected" || rawStatus === "reject" || rawStatus === "rejected";
      } catch {
        return false;
      }
    }

    // Consultant info (stored at login, used in profile page)
    public static setConsultantInfo(info: {
      id: number;
      email: string;
      fullName: string;
      mobileNo: string;
      dailyRequestCountLimit: number;
      marriage_profile_limit: number;
    }): void {
      window.localStorage.setItem("consultantInfo", JSON.stringify(info));
    }

    public static getConsultantInfo(): {
      id: number;
      email: string;
      fullName: string;
      mobileNo: string;
      dailyRequestCountLimit: number;
      marriage_profile_limit: number;
    } | null {
      const raw = window.localStorage.getItem("consultantInfo");
      return raw ? JSON.parse(raw) : null;
    }

    // Password rules: 8-12 chars, upper+lower+digit+@, no other special chars
    public static readonly PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*@)[A-Za-z0-9@]{8,12}$/;

    public static isValidPassword(password: string): boolean {
        return GlobalUtils.PASSWORD_REGEX.test(password);
    }

    // Helper: clear session/local storage safely
    public static clearSession(): void {
      try {
        window.localStorage.removeItem("authToken");
        window.localStorage.removeItem("userRole");
        window.localStorage.removeItem("accountInfo");
        window.localStorage.removeItem("cachedSubmitDetails");
        window.localStorage.removeItem("pendingAccess");
        window.localStorage.removeItem("approved");
        window.localStorage.removeItem("applicationInfo");
        window.localStorage.removeItem("consultantInfo");
      } catch {}
    }
}

// Helper
export const getMonthAbbreviation = (monthNumber: number) => {
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    return months[monthNumber - 1];
};

export const toCamelCase = (label: string): string => {
  return label
    .split(' ')
    .map((word) => word.toLocaleLowerCase().charAt(0).toUpperCase() + word.toLocaleLowerCase().slice(1))
    .join(' ');
}
